import { useState } from 'react';

import { ManifestEntry, buildManifestPayload } from './PartsShipmentsHelpers';
import { ShipmentSubmitAction } from './ShipmentFormActions';
import { LocationNodeData } from '../../../../../@types/live-search.type';
import { Shipment } from '../../../../../@types/shipment.type';
import { LogTypeNames } from '../../../../../configs/enums';
import {
  useCreateShipment,
  useCreateShipmentServiceAllocation,
} from '../../../../../queries/shipment-query';
import { toast } from '../../../../../utils/toast';

export interface UseShipmentSubmitArgs {
  serviceId: number;
  canDispatch: boolean;
  source: LocationNodeData | null;
  destination: LocationNodeData | null;
  clientHierarchy?: string;
  clientName?: string;
  contractName?: string;
  note: string;
  ref1: string;
  bypassReceipt: boolean;
  manifest: ManifestEntry[];
  onCreated: (shipment: Shipment, action: ShipmentSubmitAction) => void;
}

/**
 * The create-and-link submit flow for the job-scoped shipment form. Two
 * non-atomic writes: POST /shipment, then POST /shipmentServiceAllocation —
 * a link failure must surface the orphan shipment ID (see PR #324), never the
 * misleading "Failed to create shipment".
 */
export const useShipmentSubmit = ({
  serviceId,
  canDispatch,
  source,
  destination,
  clientHierarchy,
  clientName,
  contractName,
  note,
  ref1,
  bypassReceipt,
  manifest,
  onCreated,
}: UseShipmentSubmitArgs) => {
  const [saving, setSaving] = useState(false);
  const [pendingAction, setPendingAction] = useState<ShipmentSubmitAction | null>(null);
  const [error, setError] = useState('');

  const createShipment = useCreateShipment();
  const linkShipment = useCreateShipmentServiceAllocation();

  const handleSubmit = async (action: ShipmentSubmitAction) => {
    // The submit buttons disable while saving, but a rapid double-click can
    // fire before the re-render lands — drop the second call so we never
    // create two shipments.
    if (saving) {
      return;
    }
    if (!canDispatch) {
      setError('You do not have permission to dispatch shipments');
      return;
    }
    if (!source) {
      setError('Source is required');
      return;
    }
    if (!destination) {
      setError('Destination is required');
      return;
    }
    // Defence-in-depth: the action buttons are disabled when the manifest is
    // empty, but if a programmatic submit somehow reaches here the gate still
    // blocks the create. Surface the same warning toast users see in standalone
    // dispatch so the failure is visible.
    if (manifest.length === 0) {
      toast.warning('Add at least one part to the manifest before creating the shipment');
      return;
    }
    setError('');
    setPendingAction(action);
    setSaving(true);
    let shipment: Shipment;
    try {
      shipment = await createShipment.mutateAsync({
        source: source.name,
        sourceNodeId: source.id,
        destination: destination.name,
        destinationNodeId: destination.id,
        shipmentStatusCode: 'in-transit',
        shipmentTypeCode: 'dispatch',
        clientHierarchy: clientHierarchy ?? '',
        clientHierarchyName: `${clientName ?? ''} → ${contractName ?? ''}`,
        client: clientName ?? '',
        contract: contractName ? [contractName] : [],
        serviceId,
        connoteNo: '',
        note: note || undefined,
        referenceNumber1: ref1 || undefined,
        bypassReceipt,
        manifest: buildManifestPayload(manifest),
      });
    } catch {
      setError('Failed to create shipment');
      setSaving(false);
      setPendingAction(null);
      return;
    }
    // Separate try so a link failure surfaces the orphan shipment ID instead of
    // the misleading "Failed to create shipment" — the shipment row already
    // exists on the backend even though it isn't linked to this job.
    try {
      await linkShipment.mutateAsync({
        shipmentId: shipment.id,
        serviceId,
        logType: LogTypeNames.NewShipmentCreated,
      });
    } catch {
      setError(
        `Shipment AM-SID-${shipment.id} was created but couldn't be linked to this job. ` +
          `Cancel this form and use "Link Existing Shipment" with ID ${shipment.id} to attach it manually.`
      );
      setSaving(false);
      setPendingAction(null);
      return;
    }
    onCreated(shipment, action);
    setSaving(false);
    setPendingAction(null);
  };

  return {
    saving,
    pendingAction,
    error,
    setError,
    handleSubmit,
  };
};
